import { FC, RefObject, useEffect, useRef } from 'react';
import { hooks } from '../hooks/index.js';
import { EventHandler, Events } from './events.js';
import { create, load } from './script-loader.js';
import { getTinymce } from './tiny-mce.js';
import {
  bindHandlers,
  isFunction,
  isTextarea,
  mergePlugins,
  uuid,
} from './utils.js';

export type EditorProps = {
  id: string;
  inline: boolean;
  initialValue: string;
  onEditorChange: EventHandler<any>;
  value: string;
  init: Record<string, any>;
  cdnTinyMce: string;
  plugins: string | string[];
  toolbar: string | string[];
  disabled: boolean;
  textareaName: string;
};

export type EditorAllProps = Partial<EditorProps> & Partial<Events>;

const scriptState = create();

type EditorApi = {
  getContent: () => string;
  setContent: (val) => string;
  setMode: (val) => string;
  initialized?: boolean;
  current?;
  on: (key, fn: (e: Event) => void) => void;
};

export const Editor: FC<EditorAllProps> = (props) => {
  const inline = props.inline ? props.inline : props.init && props.init.inline;
  const idRef = useRef<string>(props.id || uuid('tiny-react'));
  const elementRef = useRef<HTMLElement>(null);
  const editorRef = useRef<EditorApi | null>(null);
  const currentContentRef = useRef<string | null>(null);
  const boundHandlersRef = useRef<Record<string, EventHandler<any>>>({});

  const initEditor = hooks.useCallbackRef(
    (initEvent: Event, editor: EditorApi) => {
      const value =
        typeof props.value === 'string'
          ? props.value
          : typeof props.initialValue === 'string'
            ? props.initialValue
            : '';

      editor.setContent(value);
      if (isFunction(props.onEditorChange)) {
        editor.on('change keyup setcontent', () => {
          const newContent = editor.getContent();
          if (newContent !== currentContentRef.current) {
            currentContentRef.current = newContent;
            if (props.onEditorChange && isFunction(props.onEditorChange)) {
              props.onEditorChange(currentContentRef.current, editor);
            }
          }
        });
      }
      if (props.onInit && isFunction(props.onInit)) {
        props.onInit(initEvent, editor);
      }
      bindHandlers(editor, props, boundHandlersRef.current);
    }
  );

  const initialize = hooks.useCallbackRef(() => {
    if (isTextarea(elementRef.current)) {
      elementRef.current.style.visibility = '';
    }

    getTinymce().init({
      ...props.init,
      target: elementRef.current,
      readonly: props.disabled,
      inline: inline,
      plugins: mergePlugins(props.init && props.init.plugins, props.plugins),
      toolbar: props.toolbar || (props.init && props.init.toolbar),
      setup: (editor: EditorApi) => {
        editor.on('init', (e: Event) => {
          initEditor(e, editor);
        });
        editorRef.current = editor;
        if (props.init && typeof props.init.setup === 'function') {
          props.init.setup(editorRef.current);
        }
      },
    });
  });

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.initialized) {
      if (props.value && props.value !== currentContentRef.current) {
        editor.setContent(props.value);
      }
    }
  }, [props.value]);

  hooks.useUpdateEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.initialized) {
      bindHandlers(editor, props, boundHandlersRef.current);
      currentContentRef.current =
        currentContentRef.current || editor.getContent();
      if (props.value && props.value !== currentContentRef.current) {
        editor.setContent(props.value);
      }
      if (props.disabled) {
        editor.setMode(props.disabled ? 'readonly' : 'design');
      }
    }
  }, []);

  useEffect(() => {
    const tinyMce = getTinymce();
    if (tinyMce !== null) {
      initialize();
    } else if (elementRef.current && elementRef.current.ownerDocument) {
      const doc = elementRef.current.ownerDocument;
      // `https://cdn.tiny.cloud/1/${apiKey}/tinymce/${channel}/tinymce.min.js`
      load(scriptState, doc, props.cdnTinyMce || '', initialize);
    }
    return () => {
      if (tinyMce !== null) {
        tinyMce.remove(editorRef.current);
      }
    };
  }, [initialize, props.cdnTinyMce]);

  return inline ? (
    <div ref={elementRef as RefObject<HTMLDivElement>} id={idRef.current}></div>
  ) : (
    <textarea
      ref={elementRef as RefObject<HTMLTextAreaElement>}
      style={{ visibility: 'hidden' }}
      name={props.textareaName}
      id={idRef.current}
    />
  );
};
